import isEqual from 'lodash/isEqual';
import { Delta } from 'quill/core';
import DiffAccordion from '../formats/DiffAccordion';
import createQuillInstance from './createQuillInstance';
var DiffStatus;
(function (DiffStatus) {
    DiffStatus[DiffStatus["INSERTED"] = 1] = "INSERTED";
    DiffStatus[DiffStatus["DELETED"] = 2] = "DELETED";
    DiffStatus[DiffStatus["RETAINED"] = 3] = "RETAINED";
})(DiffStatus || (DiffStatus = {}));
const DEFAULT_DIFF_CONTEXT_LINES = 2;
function isChangedDiffStatus(op) {
    const diffStatus = op.attributes?.diffStatus;
    return diffStatus === DiffStatus.INSERTED || diffStatus === DiffStatus.DELETED;
}
function appendOpToDelta(delta, op) {
    if (op.insert !== undefined) {
        delta.insert(op.insert, op.attributes);
    }
    else if (op.retain) {
        delta.retain(op.retain, op.attributes);
    }
    else if (op.delete) {
        delta.delete(op.delete);
    }
}
function splitDeltaIntoLines(delta) {
    const lines = [{ ops: [], hasChanges: false }];
    const finalizeLine = () => {
        lines.push({ ops: [], hasChanges: false });
    };
    for (let i = 0; i < delta.ops.length; i++) {
        const op = delta.ops[i];
        const isChanged = isChangedDiffStatus(op);
        lines[lines.length - 1].hasChanges =
            lines[lines.length - 1].hasChanges || isChanged;
        if (typeof op.insert === 'string') {
            const parts = op.insert.split('\n');
            for (let j = 0; j < parts.length; j++) {
                const part = parts[j];
                const activeLine = lines[lines.length - 1];
                if (part) {
                    activeLine.ops.push({
                        insert: part,
                        attributes: op.attributes,
                    });
                }
                if (j < parts.length - 1) {
                    activeLine.ops.push({
                        insert: '\n',
                        attributes: op.attributes,
                    });
                    finalizeLine();
                    lines[lines.length - 1].hasChanges =
                        lines[lines.length - 1].hasChanges || isChanged;
                }
            }
            continue;
        }
        lines[lines.length - 1].ops.push(op);
    }
    const lastLine = lines[lines.length - 1];
    if (lastLine.ops.length === 0 && lines.length > 1) {
        lines.pop();
    }
    return lines;
}
function getMergedRanges(changedLineIndexes, totalLines, contextLines) {
    if (!changedLineIndexes.length) {
        return [];
    }
    const ranges = changedLineIndexes.map(lineIndex => ({
        start: Math.max(0, lineIndex - contextLines),
        end: Math.min(totalLines - 1, lineIndex + contextLines),
    }));
    const mergedRanges = [ranges[0]];
    for (let i = 1; i < ranges.length; i++) {
        const currentRange = ranges[i];
        const prevRange = mergedRanges[mergedRanges.length - 1];
        if (currentRange.start <= prevRange.end + 1) {
            prevRange.end = Math.max(prevRange.end, currentRange.end);
        }
        else {
            mergedRanges.push(currentRange);
        }
    }
    return mergedRanges;
}
function getHiddenLinesLabel(hiddenLines) {
    return hiddenLines === 1 ? 'Show 1 line' : `Show ${hiddenLines} lines`;
}
function cloneOp(op) {
    return {
        ...op,
        attributes: op.attributes ? { ...op.attributes } : undefined,
    };
}
function getHiddenRangeOps(lines, startLine, endLine) {
    const hiddenOps = [];
    for (let lineIndex = startLine; lineIndex <= endLine; lineIndex++) {
        const line = lines[lineIndex];
        for (let opIndex = 0; opIndex < line.ops.length; opIndex++) {
            hiddenOps.push(cloneOp(line.ops[opIndex]));
        }
    }
    return hiddenOps;
}
function insertCollapsedLinesMarker(delta, theme, lines, hiddenLines, startLine, endLine) {
    if (hiddenLines <= 0) {
        return;
    }
    const hiddenLabel = getHiddenLinesLabel(hiddenLines);
    const value = {
        title: hiddenLabel,
        body: { ops: getHiddenRangeOps(lines, startLine, endLine) },
    };
    delta.insert({ [DiffAccordion.blotName]: value });
}
function getCollapsedDiffViewDelta(theme, diffViewDelta, contextLines = DEFAULT_DIFF_CONTEXT_LINES) {
    const lines = splitDeltaIntoLines(diffViewDelta);
    const changedLineIndexes = [];
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].hasChanges) {
            changedLineIndexes.push(i);
        }
    }
    const mergedRanges = getMergedRanges(changedLineIndexes, lines.length, contextLines);
    const collapsedDelta = new Delta();
    const hasLeadingGap = mergedRanges.length > 0 && mergedRanges[0].start > 0;
    if (hasLeadingGap) {
        const hiddenLeadingLines = mergedRanges[0].start;
        insertCollapsedLinesMarker(collapsedDelta, theme, lines, hiddenLeadingLines, 0, mergedRanges[0].start - 1);
    }
    for (let i = 0; i < mergedRanges.length; i++) {
        const range = mergedRanges[i];
        for (let lineIndex = range.start; lineIndex <= range.end; lineIndex++) {
            const line = lines[lineIndex];
            for (let opIndex = 0; opIndex < line.ops.length; opIndex++) {
                appendOpToDelta(collapsedDelta, line.ops[opIndex]);
            }
        }
        const hasMoreHunks = i < mergedRanges.length - 1;
        if (hasMoreHunks) {
            const nextRange = mergedRanges[i + 1];
            const hiddenLinesBetween = nextRange.start - range.end - 1;
            insertCollapsedLinesMarker(collapsedDelta, theme, lines, hiddenLinesBetween, range.end + 1, nextRange.start - 1);
        }
    }
    const hasTrailingGap = mergedRanges.length > 0 &&
        mergedRanges[mergedRanges.length - 1].end < lines.length - 1;
    if (hasTrailingGap) {
        const hiddenTrailingLines = lines.length - mergedRanges[mergedRanges.length - 1].end - 1;
        insertCollapsedLinesMarker(collapsedDelta, theme, lines, hiddenTrailingLines, mergedRanges[mergedRanges.length - 1].end + 1, lines.length - 1);
    }
    return collapsedDelta;
}
function attributeInsNDel(diffDelta) {
    const diffViewDelta = new Delta();
    for (let i = 0; i < diffDelta.ops.length; i++) {
        const op = diffDelta.ops[i];
        // if the change was an insertion
        if (op.insert) {
            diffViewDelta.insert(op.insert, {
                ...op.attributes,
                diffStatus: DiffStatus.INSERTED,
            });
        }
        else if (op.delete) {
            diffViewDelta.retain(op.delete, {
                ...op.attributes,
                diffStatus: DiffStatus.DELETED,
            });
        }
        else if (op.retain) {
            diffViewDelta.retain(op.retain, {
                ...op.attributes,
                diffStatus: DiffStatus.RETAINED,
            });
        }
    }
    return diffViewDelta;
}
function updateDiffStyles(theme, delta) {
    for (let i = 0; i < delta.ops.length; ++i) {
        const op = delta.ops[i];
        if (op.attributes?.diffStatus) {
            if (op.insert && typeof op.insert !== 'string') {
                op.attributes.padding = `${theme.spacing.standard.md}px`;
                op.attributes.display = 'block';
            }
            if (op.attributes.diffStatus === DiffStatus.INSERTED) {
                op.attributes.background = theme.palette.background.positive;
            }
            else if (op.attributes.diffStatus === DiffStatus.DELETED) {
                op.attributes.background = theme.palette.background.negative;
                op.attributes.strike = true;
            }
        }
    }
    return delta;
}
export function getDiffViewDelta(theme, quill, oldHTML, newHTML, options = {}) {
    const oldDelta = quill.clipboard.convert({ html: oldHTML });
    const newDelta = quill.clipboard.convert({ html: newHTML });
    const diffDelta = oldDelta.diff(newDelta);
    const diffViewDelta = updateDiffStyles(theme, oldDelta.compose(attributeInsNDel(diffDelta)));
    if (!options.showOnlyDiff) {
        return diffViewDelta;
    }
    const collapsedDelta = getCollapsedDiffViewDelta(theme, diffViewDelta, options.contextLines ?? DEFAULT_DIFF_CONTEXT_LINES);
    return collapsedDelta.ops.length ? collapsedDelta : diffViewDelta;
}
export function getDiffDelta(oldHTML, newHTML, quill = createQuillInstance(document.createElement('div'), { readOnly: false })) {
    const oldDelta = typeof oldHTML === 'string' ? quill.clipboard.convert({ html: oldHTML }) : new Delta(oldHTML);
    const newDelta = typeof newHTML === 'string' ? quill.clipboard.convert({ html: newHTML }) : new Delta(newHTML);
    return oldDelta.diff(newDelta);
}
export function doesDeltaContainInsOrDel(delta) {
    return delta.ops.some(op => {
        return op.insert || op.delete;
    });
}
export function areEqualDeltas(a, b) {
    const aOps = typeof a === 'object' ? a.ops : a;
    const bOps = typeof b === 'object' ? b.ops : b;
    return aOps.every((op, index) => {
        const bOp = bOps[index];
        return op.insert === bOp.insert && op.delete === bOp.delete && op.retain === bOp.retain && isEqual(op.attributes, bOp.attributes);
    });
}
